import React from 'react';
import { Anchor, Button, Group, Stack, Text, TextInput, ThemeIcon } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthFrame from '@/components/auth/frame/Auth.Frame';
import AuthFlow from '@/components/auth/flow/Auth.Flow';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import useAuthFlow from '@/hooks/use.auth.flow';
import flowMessageOf from '@/function/auth/flow.message.of';
import flowTitleOf from '@/function/auth/flow.title.of';
import emailInputClean, { EMAIL_MAX } from '@/function/auth/email.input.clean';
import emailValid from '@/function/auth/email.valid';
import { RESET_COPY } from './config/reset.labels';

const Reset: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const flow = useAuthFlow({ kind: 'recovery' });
    const [email, setEmail] = React.useState(() => emailInputClean(String((location.state as { email?: unknown } | null)?.email ?? '')));
    const [local, setLocal] = React.useState<string | null>(null);
    const message = React.useMemo(() => (flow.failure && flow.screen.id === 'idle' ? flowMessageOf(flow.failure, 'idle') : null), [flow.failure, flow.screen.id]);
    const toLogin = () => navigate('/auth/login', { replace: true, state: { email: flow.hint ?? email } });

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (flow.busy) return;
        const address = email.trim();
        if (!emailValid(address)) {
            setLocal('Questo indirizzo non sembra un’email');
            return;
        }
        void flow.start(address);
    };

    const start = (
        <form onSubmit={submit} noValidate>
            <Stack gap="md">
                <Text size="sm" c="dimmed">{RESET_COPY.intro}</Text>
                <FlowAlert message={message && message.field !== 'email' ? message : null} />
                <TextInput
                    withAsterisk
                    label="Email"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    maxLength={EMAIL_MAX}
                    value={email}
                    error={local ?? (message?.field === 'email' ? message.text : undefined)}
                    onChange={(event) => { setLocal(null); setEmail(emailInputClean(event.currentTarget.value)); }}
                />
                <Button type="submit" mih={44} loading={flow.busy} disabled={flow.busy}>Mandami il codice</Button>
            </Stack>
        </form>
    );

    const done = (
        <Stack align="center" gap="md" role="status">
            <ThemeIcon size={56} radius="xl" variant="light" color="success">
                <IconCircleCheck size={32} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">{RESET_COPY.done}</Text>
            <Button fullWidth mih={44} onClick={toLogin}>Entra</Button>
        </Stack>
    );

    return (
        <AuthFrame
            title={flow.screen.id === 'done' ? RESET_COPY.doneTitle : flowTitleOf('recovery', flow.screen, RESET_COPY.title)}
            subtitle={flow.hint && flow.screen.id !== 'idle' && flow.screen.id !== 'done' ? `Per ${flow.hint}` : RESET_COPY.subtitle}
        >
            <AuthFlow flow={flow} start={start} done={done} onRestart={() => flow.reset()} newPasswordLabel="Salva la password" />
            {flow.screen.id !== 'done' ? (
                <Group justify="center" gap={5} mt="sm">
                    <Text size="sm" c="dimmed">Ti sei ricordato la password?</Text>
                    <Anchor component="button" type="button" size="sm" style={{ minHeight: 44 }} disabled={flow.busy} onClick={toLogin}>Entra</Anchor>
                </Group>
            ) : null}
        </AuthFrame>
    );
};

export default Reset;
